import { RouterErrorBoundary } from "@/components/error-boundary/error-boundary";
import Loading from "@/components/loading/loading";
import type { ComponentType, ReactNode } from "react";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import RootLayout from "./root-layout";
import { ADMIN_HOME_PATH, HOME_PATH, LOGIN_PATH } from "@/utils/global-constants";

import NotFoundView from "@app/not-found/not-found-view";

const lazyWithErrorHandling = <T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>,
) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      const isChunkLoadError =
        message.includes("Failed to fetch dynamically imported module") ||
        message.includes("error loading dynamically imported module") ||
        message.includes("Unable to preload CSS") ||
        message.includes("text/html");

      if (isChunkLoadError) {
        window.location.reload();
      }

      throw error;
    }
  });
};

const LoginView = lazyWithErrorHandling(() => import("@app/login-view"));
const FindPasswordLayout = lazyWithErrorHandling(
  () => import("@app/find-password/find-password-layout"),
);
const FindPasswordRequestLink = lazyWithErrorHandling(
  () => import("@app/find-password/request-link/request-link-view"),
);
const FindPasswordRequestComplete = lazyWithErrorHandling(
  () => import("@app/find-password/request-complete/request-complete-view"),
);
const FindPasswordResetPassword = lazyWithErrorHandling(
  () => import("@app/find-password/[token]/reset-password-view"),
);
const InitPasswordView = lazyWithErrorHandling(
  () => import("@app/init-password/init-password-view"),
);
const PrivacyView = lazyWithErrorHandling(() => import("@app/privacy/privacy-view"));
const TermsView = lazyWithErrorHandling(() => import("@app/terms/terms-view"));
const AppAccountSettingLayout = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/app-account-setting-layout"),
);
const AppAccountSettingView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/app-account-setting-view"),
);
const AppAccountSettingCheckNameView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/check-name/check-name-view"),
);
const AppAccountSettingParentalConsentView = lazyWithErrorHandling(
  () =>
    import(
      "@app/(auth-approved-patient)/app-account-setting/parental-consent/parental-consent-view"
    ),
);
const AppAccountSettingSetupAccountView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/setup-account/setup-account-view"),
);
const AppAccountSettingCompleteView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/complete/complete-view"),
);
const AppAccountSettingExpiredView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/expired/expired-view"),
);

const DoctorHomeLayout = lazyWithErrorHandling(() => import("@app/(auth_doctor)/home/home-layout"));
const DoctorHomeView = lazyWithErrorHandling(() => import("@app/(auth_doctor)/home/home-view"));
const DoctorPasswordChangeView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/(my-info)/password-change/password-change-view"),
);
const DoctorPatientView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/patient/patient-view"),
);
const DoctorDoctorView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/doctor/doctor-view"),
);
const DoctorPrivacyView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/privacy/privacy-view"),
);
const DoctorTermsView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/terms/terms-view"),
);
const DoctorNotificationView = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/notification/notification-view"),
);

const AdminHomeLayout = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/admin-home-layout"),
);
const AdminHomeView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/admin-home-view"),
);
const AdminPasswordChangeView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/(my-info)/password-change/password-change-view"),
);
const AdminPatientView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/patient/patient-view"),
);
const AdminDoctorView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/doctor/doctor-view"),
);
const AdminHospitalView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/hospital/hospital-view"),
);
const AdminLogView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/log/log-view"),
);
const AdminPrivacyView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/privacy/privacy-view"),
);
const AdminTermsView = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/terms/terms-view"),
);

function SuspenseWrapper({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

const loginRouteElement = (
  <SuspenseWrapper>
    <LoginView />
  </SuspenseWrapper>
);

function RootlessRedirect() {
  const location = useLocation();
  const rootlessPath = location.pathname.replace(/^\/root(?=\/|$)/, "") || LOGIN_PATH;
  const currentPath = rootlessPath.replace(/^\/main(?=\/|$)/, HOME_PATH);
  return <Navigate to={`${currentPath}${location.search}${location.hash}`} replace />;
}

function MainToHomeRedirect() {
  const location = useLocation();
  const homePath = location.pathname.replace(/^\/main(?=\/|$)/, HOME_PATH);
  return <Navigate to={`${homePath}${location.search}${location.hash}`} replace />;
}

function ResetSnRedirect() {
  const location = useLocation();
  return <Navigate to={`/find-password/reset${location.search}${location.hash}`} replace />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      { index: true, element: loginRouteElement },
      { path: "root", element: <Navigate to={LOGIN_PATH} replace /> },
      { path: "root/*", element: <RootlessRedirect /> },
      { path: "main", element: <Navigate to={HOME_PATH} replace /> },
      { path: "main/*", element: <MainToHomeRedirect /> },
      {
        path: "login",
        element: <Navigate to={LOGIN_PATH} replace />,
      },
      {
        path: "find-password",
        element: (
          <SuspenseWrapper>
            <FindPasswordLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/find-password/request-link" replace />,
          },
          {
            path: "request-link",
            element: (
              <SuspenseWrapper>
                <FindPasswordRequestLink />
              </SuspenseWrapper>
            ),
          },
          {
            path: "request-complete",
            element: (
              <SuspenseWrapper>
                <FindPasswordRequestComplete />
              </SuspenseWrapper>
            ),
          },
          {
            path: "reset",
            element: (
              <SuspenseWrapper>
                <FindPasswordResetPassword />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":token",
            element: (
              <SuspenseWrapper>
                <FindPasswordResetPassword />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      { path: "sendresetemail", element: <Navigate to="/find-password/request-link" replace /> },
      { path: "resetsn", element: <ResetSnRedirect /> },
      { path: "changesn", element: <Navigate to="/home/password-change" replace /> },
      {
        path: "init-password",
        element: <Navigate to="/" replace />,
      },
      {
        path: "init-password/:token",
        element: (
          <SuspenseWrapper>
            <InitPasswordView />
          </SuspenseWrapper>
        ),
      },
      {
        path: "privacy",
        element: (
          <SuspenseWrapper>
            <PrivacyView />
          </SuspenseWrapper>
        ),
      },
      {
        path: "terms",
        element: (
          <SuspenseWrapper>
            <TermsView />
          </SuspenseWrapper>
        ),
      },
      {
        path: "app-account-setting",
        element: (
          <SuspenseWrapper>
            <AppAccountSettingLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/" replace />,
          },
          {
            path: ":token",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "check-name",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingCheckNameView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "parental-consent",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingParentalConsentView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "setup-account",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingSetupAccountView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "complete",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingCompleteView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "expired",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingExpiredView />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      { path: "patient_video", element: <Navigate to={LOGIN_PATH} replace /> },
      { path: "home/dashboard", element: <Navigate to={HOME_PATH} replace /> },
      { path: "home/log", element: <Navigate to={`${ADMIN_HOME_PATH}/log`} replace /> },
      {
        path: "home",
        element: (
          <SuspenseWrapper>
            <DoctorHomeLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <DoctorHomeView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "dashboard",
            element: <Navigate to={HOME_PATH} replace />,
          },
          {
            path: "password-change",
            element: (
              <SuspenseWrapper>
                <DoctorPasswordChangeView />
              </SuspenseWrapper>
            ),
          },
          { path: "statis", element: <NotFoundView /> },
          { path: "patient/new", element: <NotFoundView /> },
          { path: "patient/edit/:patientUuid", element: <NotFoundView /> },
          {
            path: "patient/*",
            element: (
              <SuspenseWrapper>
                <DoctorPatientView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctor",
            element: (
              <SuspenseWrapper>
                <DoctorDoctorView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctor/:doctorUuid",
            element: (
              <SuspenseWrapper>
                <DoctorDoctorView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "privacy",
            element: (
              <SuspenseWrapper>
                <DoctorPrivacyView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "terms",
            element: (
              <SuspenseWrapper>
                <DoctorTermsView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "notification",
            element: (
              <SuspenseWrapper>
                <DoctorNotificationView />
              </SuspenseWrapper>
            ),
          },
          { path: "*", element: <NotFoundView /> },
        ],
      },
      {
        path: "admin/home",
        element: (
          <SuspenseWrapper>
            <AdminHomeLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <AdminHomeView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "dashboard",
            element: <Navigate to={ADMIN_HOME_PATH} replace />,
          },
          {
            path: "hospital/*",
            element: (
              <SuspenseWrapper>
                <AdminHospitalView />
              </SuspenseWrapper>
            ),
          },
          { path: "statis", element: <NotFoundView /> },
          { path: "patient/new", element: <NotFoundView /> },
          { path: "patient/edit/:patientUuid", element: <NotFoundView /> },
          {
            path: "patient/*",
            element: (
              <SuspenseWrapper>
                <AdminPatientView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "password-change",
            element: (
              <SuspenseWrapper>
                <AdminPasswordChangeView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctor",
            element: (
              <SuspenseWrapper>
                <AdminDoctorView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctor/:doctorUuid",
            element: (
              <SuspenseWrapper>
                <AdminDoctorView />
              </SuspenseWrapper>
            ),
          },
          { path: "doctor/new", element: <NotFoundView /> },
          { path: "doctor/edit", element: <NotFoundView /> },
          { path: "doctor/edit/:doctorUuid", element: <NotFoundView /> },
          {
            path: "privacy",
            element: (
              <SuspenseWrapper>
                <AdminPrivacyView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "terms",
            element: (
              <SuspenseWrapper>
                <AdminTermsView />
              </SuspenseWrapper>
            ),
          },
          {
            path: "log",
            element: (
              <SuspenseWrapper>
                <AdminLogView />
              </SuspenseWrapper>
            ),
          },
          { path: "*", element: <NotFoundView /> },
        ],
      },
      { path: "*", element: <NotFoundView /> },
    ],
  },
]);

export default router;
